import DownloadIcon from '@mui/icons-material/Download';
import { Box, Card, CardContent, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { downloadFile, metricsToCSV } from '../../../utils/exportUtils';

interface VoicemailTeam {
  teamId: number;
  teamName: string;
  totalVoicemails: number;
  inboundVoicemails: number;
  outboundVoicemails: number;
  averageDuration: number;
}

interface VoicemailsTeamsTabProps {
  teams: VoicemailTeam[];
  comparison?: VoicemailTeam[];
  renderMetricCard: (
    title: string,
    value: number | string,
    comparison?: number | string,
    rawValue?: number,
    invertColors?: boolean,
  ) => JSX.Element;
  formatDuration: (seconds: number) => string;
  currentStartDate?: string;
  currentEndDate?: string;
  previousStartDate?: string;
  previousEndDate?: string;
}

export const VoicemailsTeamsTab = ({
  teams,
  comparison,
  renderMetricCard,
  formatDuration,
  currentStartDate,
  currentEndDate,
  previousStartDate,
  previousEndDate,
}: VoicemailsTeamsTabProps) => {
  const handleExportTeam = (team: VoicemailTeam, comparisonTeam?: VoicemailTeam) => {
    if (!currentStartDate || !currentEndDate) return;

    const teamMetrics = [
      { label: 'Team Name', value: team.teamName, previousValue: comparisonTeam?.teamName },
      { label: 'Total Voicemails', value: team.totalVoicemails, previousValue: comparisonTeam?.totalVoicemails },
      { label: 'Inbound', value: team.inboundVoicemails, previousValue: comparisonTeam?.inboundVoicemails },
      { label: 'Outbound', value: team.outboundVoicemails, previousValue: comparisonTeam?.outboundVoicemails },
      { label: 'Average Duration (seconds)', value: team.averageDuration, previousValue: comparisonTeam?.averageDuration },
    ];

    const csv = metricsToCSV(teamMetrics, currentStartDate, currentEndDate, previousStartDate, previousEndDate);
    downloadFile(csv, `voicemail-team-${team.teamName.replace(/\s+/g, '-').toLowerCase()}-metrics.csv`);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant='h6' gutterBottom>
        Team Metrics
      </Typography>
      <Grid container spacing={3}>
        {teams.map((team) => {
          const comparisonTeam = comparison?.find((t) => t.teamId === team.teamId);
          return (
            <Grid item xs={12} key={team.teamId}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant='h6'>{team.teamName}</Typography>
                    <Tooltip title='Export team metrics'>
                      <IconButton size='small' onClick={() => handleExportTeam(team, comparisonTeam)} sx={{ color: '#1a667a' }}>
                        <DownloadIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard(
                        'Total Voicemails',
                        team.totalVoicemails,
                        comparisonTeam?.totalVoicemails,
                        undefined,
                        false,
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard(
                        'Inbound',
                        team.inboundVoicemails,
                        comparisonTeam?.inboundVoicemails,
                        undefined,
                        false,
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard(
                        'Outbound',
                        team.outboundVoicemails,
                        comparisonTeam?.outboundVoicemails,
                        undefined,
                        false,
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {renderMetricCard(
                        'Avg Duration',
                        formatDuration(team.averageDuration),
                        comparisonTeam?.averageDuration,
                        team.averageDuration,
                        false,
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
